import { msg } from '@lingui/core/macro';
import { TASK_SCALE_TYPE } from '@routes/sidebar/___shared___/constants.ts';
import type { TaskScaleType } from '@routes/sidebar/___shared___/types.ts';
import { BoldText } from '@routes/sidebar/admin-center/creator/___pages___/elements/ui/task-element/ui/modals/rating-scale/styled.tsx';
import type {
    MyLearningTaskDetailsSubtask,
    RatingPoints,
} from '@routes/sidebar/learning/___pages___/learning-overview/api/task.api.ts';
import { formatDateBasedOnPreferences } from '@utils/date.ts';
import { generateUniqueKeys } from '@utils/generators.ts';
import { translateItem } from '@utils/translations.ts';
import styled from 'styled-components';

import {
    GuidelineCell,
    SubtasksCell,
    SubtasksHeaderCell,
    SubtasksHeaderRow,
    SubtasksRow,
    SubtasksTable,
} from './styled';

const SUBTASK_HEADERS = [msg`Task`, msg`Assessors guidelines`, msg`Self assessment`, msg`Assessor's rating`, msg`Date`];
const TWO_POINT_APPROVED_POINTS = 2;

const isTwoPointScale = (ratingScale: TaskScaleType) =>
    ratingScale === TASK_SCALE_TYPE.TwoPointApprove || ratingScale === TASK_SCALE_TYPE.TwoPointConfidence;

export const Rating = styled(BoldText)<{
    $points: RatingPoints;
    $ratingScale: TaskScaleType;
}>`
    color: ${({ $points, $ratingScale }) => {
        if (isTwoPointScale($ratingScale)) {
            return $points === TWO_POINT_APPROVED_POINTS
                ? 'var(--colors-text-success, #22A06B)'
                : 'var(--colors-red-450, #E14B53)';
        }

        switch ($points) {
            case 4:
            case 5:
                return 'var(--colors-text-success, #22A06B)';
            case 1:
            case 2:
                return 'var(--colors-red-450, #E14B53)';
            case 0:
            case 3:
            default:
                return 'var(--colors-text-headings, #3E445C)';
        }
    }};
`;

export const getRatingLabel = (points: RatingPoints, ratingScale: TaskScaleType) => {
    if (ratingScale === TASK_SCALE_TYPE.TwoPointApprove) {
        return points === TWO_POINT_APPROVED_POINTS ? msg`Approved` : msg`Disapproved`;
    }

    if (ratingScale === TASK_SCALE_TYPE.TwoPointConfidence) {
        return points === TWO_POINT_APPROVED_POINTS ? msg`Confident` : msg`Not Confident`;
    }

    switch (points) {
        case 5:
            return msg`Exceptional`;
        case 4:
            return msg`Exceeds expectations`;
        case 3:
            return msg`Meets expectations`;
        case 2:
            return msg`Needs improvement`;
        case 1:
            return msg`Unsatisfactory`;
        case 0:
        default:
            return '';
    }
};

export const renderSubtasks = (
    subtasks: MyLearningTaskDetailsSubtask[],
    dateFormat: string,
    ratingScale: TaskScaleType,
) => (
    <SubtasksTable>
        <SubtasksHeaderRow>
            {SUBTASK_HEADERS.map((header) => (
                <SubtasksHeaderCell key={header.id}>{translateItem(header)}</SubtasksHeaderCell>
            ))}
        </SubtasksHeaderRow>

        {generateUniqueKeys(subtasks).map(
            ({ item: { description, guideline, selfAssessmentPoints, assessorRatingPoints, date }, key }) => (
                <SubtasksRow key={key}>
                    <SubtasksCell>{description}</SubtasksCell>

                    <GuidelineCell>{guideline}</GuidelineCell>

                    <SubtasksCell>
                        {selfAssessmentPoints !== null && (
                            <Rating $points={selfAssessmentPoints} $ratingScale={ratingScale}>
                                {translateItem(getRatingLabel(selfAssessmentPoints, ratingScale))}
                            </Rating>
                        )}
                    </SubtasksCell>

                    <SubtasksCell>
                        {assessorRatingPoints !== null && (
                            <Rating $points={assessorRatingPoints} $ratingScale={ratingScale}>
                                {translateItem(getRatingLabel(assessorRatingPoints, ratingScale))}
                            </Rating>
                        )}
                    </SubtasksCell>

                    <SubtasksCell>{date !== null && formatDateBasedOnPreferences(date, dateFormat)}</SubtasksCell>
                </SubtasksRow>
            ),
        )}
    </SubtasksTable>
);
